import { useEffect, type RefObject } from "react";

/**
 * Observe a SuperDoc toolbar container (within a scroll parent) and hide items
 * that overflow the available width. Items are hidden right-to-left (lowest
 * priority last) by toggling the `sd-toolbar-hidden` class. A
 * `data-toolbar-overflow` attribute on the container signals CSS that the
 * responsive mode is active.
 *
 * The hook is intentionally side-effectful (DOM mutations on a third-party
 * component) — the alternative would be re-implementing SuperDoc's toolbar,
 * which is not viable for a contained third-party editor.
 *
 * Re-runs only when `active` or `scrollRef` (object identity) changes. Ensure
 * `active` is `false` until the scroll container is mounted.
 */
export function useResponsiveToolbar(scrollRef: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active || !scrollRef.current) {
      return;
    }

    const scrollEl = scrollRef.current;
    let frameId: number | null = null;
    let observer: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let cleaned = false;
    let lastHiddenCount = -1;

    function getContainer(): HTMLElement | null {
      return scrollEl.querySelector<HTMLElement>(".superdoc-toolbar-container");
    }

    function reconcile() {
      const container = getContainer();
      if (!container) {
        return;
      }

      const toolbar = container.querySelector<HTMLElement>(".superdoc-toolbar");
      if (!toolbar) {
        return;
      }

      const items = Array.from(toolbar.querySelectorAll<HTMLElement>(":scope > .sd-toolbar-item"));
      if (items.length === 0) {
        return;
      }

      const containerWidth = container.clientWidth;
      if (containerWidth <= 0) {
        return;
      }

      // Fast path: if the toolbar already fits without any hidden items, or
      // the current hidden set already makes it fit, skip the expensive
      // reveal-and-remeasure cycle to avoid a single-frame layout flash.
      if (toolbar.scrollWidth <= containerWidth) {
        // lastHiddenCount initialized to -1 as a sentinel so the first run
        // always takes the overflow path regardless of current state.
        if (lastHiddenCount <= 0) {
          container.setAttribute("data-toolbar-overflow", "false");
          return;
        }
        // Container may have grown — try revealing hidden items iteratively.
        const hiddenItems = items.filter((item) => item.classList.contains("sd-toolbar-hidden"));
        if (hiddenItems.length === 0) {
          // Items were removed from DOM (e.g. mode change) — reset state.
          lastHiddenCount = 0;
          container.setAttribute("data-toolbar-overflow", "false");
          return;
        }
        for (let i = 0; i < hiddenItems.length; i++) {
          hiddenItems[i].classList.remove("sd-toolbar-hidden");
          if (toolbar.scrollWidth > containerWidth) {
            // Went too far — re-hide and stop.
            hiddenItems[i].classList.add("sd-toolbar-hidden");
            break;
          }
          lastHiddenCount = hiddenItems.length - (i + 1);
        }
        if (lastHiddenCount === 0) {
          container.setAttribute("data-toolbar-overflow", "false");
        }
        return;
      }

      // Toolbar overflows. Reveal all items to measure natural width, then
      // hide from the end until it fits.
      for (const item of items) {
        item.classList.remove("sd-toolbar-hidden");
      }

      // Mark container as overflowing so CSS activates flex-shrink: 0.
      container.setAttribute("data-toolbar-overflow", "true");

      // Hide items from the end until the toolbar fits. We leave at least 4
      // items visible (undo/redo + font family + bold) for minimum usability.
      const MIN_VISIBLE = 4;
      let hiddenCount = 0;
      for (let i = items.length - 1; i >= MIN_VISIBLE; i--) {
        items[i].classList.add("sd-toolbar-hidden");
        hiddenCount++;
        if (toolbar.scrollWidth <= containerWidth) {
          break;
        }
      }
      lastHiddenCount = hiddenCount;
    }

    function scheduleReconcile() {
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(() => {
        frameId = null;
        if (!cleaned) {
          reconcile();
        }
      });
    }

    function setup(container: HTMLElement) {
      if (cleaned) {
        return;
      }

      // Initial reconciliation.
      reconcile();

      // Re-reconcile on container resize.
      observer = new ResizeObserver(() => scheduleReconcile());
      observer.observe(container);

      // Re-reconcile if SuperDoc mutates toolbar children (e.g. on mode change).
      mutationObserver = new MutationObserver(() => scheduleReconcile());
      mutationObserver.observe(container, { childList: true, subtree: true });
    }

    // Retry a few times in case the toolbar hasn't rendered yet.
    let attempts = 0;
    const maxAttempts = 15;
    const interval = setInterval(() => {
      if (cleaned) {
        clearInterval(interval);
        return;
      }
      attempts++;
      const container = getContainer();
      if (container?.querySelector(".superdoc-toolbar")) {
        clearInterval(interval);
        setup(container);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 200);

    return () => {
      cleaned = true;
      clearInterval(interval);
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
      observer?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [scrollRef, active]);
}
